//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import Text from '@components/atoms/text/Text';
import TimeZoneId from '@models/TimeZoneId';
import { Mixins } from '@theme/mixins';
import { getTimezoneNames } from '@utils/getTimezoneName';

interface Props {
  className?: string;
  isTimestampIndicator?: boolean;
  timeZone: TimeZoneId;
}

/**
 * The TimezoneIndicator atom displays a standardized line of text indicating
 * the timezone for a particular page's contents or the date picker on the page.
 */
const TimezoneIndicator = ({
  className = '',
  isTimestampIndicator = false,
  timeZone,
}: Props) => {
  const getTimezoneText = () => {
    const { long, short } = getTimezoneNames(timeZone);
    const text = isTimestampIndicator
      ? 'All times are displayed in'
      : 'Date ranges on this page are based on';
    return `${text} ${long} (${short})`;
  };

  return (
    <Text className={className} mixin={Mixins.Text.BodySecondary}>
      {getTimezoneText()}
    </Text>
  );
};

export const TIMEZONE_INDICATOR_HEIGHT = 29;

/**
 * The standard position for the TimezoneIndicator atom is 8px below the bottom
 * left corner of a page's main content sheet. We hardcode the height to safely use it
 * in screen margin calculations.
 */
export const TimezoneIndicatorWithMargin = styled(TimezoneIndicator)(
  ({ theme }) => `
  padding-top: ${theme.spacings[2]};
  height: ${TIMEZONE_INDICATOR_HEIGHT}px;
`,
);

export default TimezoneIndicator;
